import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import { animateNext } from 'lib/animateNext';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  selectAseChecklistItemForQuestion,
  selectAseTplChecklistItemsForQuestion,
  selectAseTplQuestion,
} from 'store/assessments/selectors';
import { Button } from 'components/Button';
import { View } from 'react-native';
import { ChecklistItemResultEnum } from 'store/assessments/types';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doAseSetChecklistItemResults } from 'store/assessments/actions';
import { useEsc } from 'hooks/useEsc';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { isEqual } from 'lodash';
import { PageHeader } from 'components/PageHeader';

export const QuickAnswerChecklistModal: React.FC<
  ModalScreenProps<ModalScreens.AseQuickAnswerChecklist>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { questionId, fieldInstanceId, instructions, actionsAfter } =
    route.params;

  const [editingChecklistItemId, setEditingChecklistItemId] = React.useState<
    number | undefined
  >(undefined);

  const question = useSelector((state) =>
    selectAseTplQuestion(state, questionId),
  );

  const checklistItems = useSelector((state) =>
    selectAseTplChecklistItemsForQuestion(state, questionId),
  );

  const checklistItemResultsFromState = useSelector((state) =>
    selectAseChecklistItemForQuestion(state, questionId, fieldInstanceId),
  );

  const [checklistItemResults, setChecklistItemResults] = React.useState<
    Record<string, ChecklistItemResultEnum | null>
  >(
    Object.fromEntries(
      Object.entries(checklistItemResultsFromState).map(([id, result]) => [
        id,
        result.result,
      ]),
    ),
  );

  const initChecklistItemResultsRef = React.useRef(checklistItemResults);

  const handleSave = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(
      doAseSetChecklistItemResults({
        questionId,
        results: checklistItemResults,
        fieldInstanceId,
        actionsAfter,
      }),
    );
  };

  const handleSaveChecklistItemResult = (
    checklistItemId: number,
    newResult: ChecklistItemResultEnum,
  ) => {
    setChecklistItemResults((current) => ({
      ...current,
      [checklistItemId]: newResult,
    }));
    setEditingChecklistItemId(undefined);
  };

  const handleDeleteChecklistItemResult = (checklistItemId: number) => {
    setChecklistItemResults((currentResults) => {
      const newResults = { ...currentResults };
      if (initChecklistItemResultsRef.current[checklistItemId] === undefined) {
        delete newResults[checklistItemId];
      } else {
        newResults[checklistItemId] = null;
      }
      return newResults;
    });
    setEditingChecklistItemId(undefined);
  };

  const isDirty = !isEqual(
    checklistItemResults,
    initChecklistItemResultsRef.current,
  );
  const handleClose = () => {
    if (isDirty) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Checklist"
          onPressCancelButton={handleClose}
          onPressSaveButton={
            Object.keys(checklistItemResults).length ? handleSave : null
          }
        />
      }
    >
      <Card bodyStyle={{ borderColor: theme.colors.yellow, borderWidth: 1 }}>
        <CardRow icon={ICONS.CircleInfoLight} value={instructions} />
      </Card>
      <CardGeneric>
        <Txt weight="bold" size="large">
          {question.question}
        </Txt>

        {!!question.description && (
          <Txt style={{ color: theme.colors.fieldValueText }}>
            {question.description}
          </Txt>
        )}
      </CardGeneric>

      {!!checklistItems.length && (
        <Card>
          {checklistItems.map((checklistItem) => {
            const checklistItemResult = checklistItemResults[checklistItem.id];
            const resultIsTick =
              checklistItemResult === ChecklistItemResultEnum.tick;
            const resultIsCross =
              checklistItemResult === ChecklistItemResultEnum.cross;
            const resultIsNA =
              checklistItemResult === ChecklistItemResultEnum.dash;
            return (
              <CardRow
                key={checklistItem.id}
                style={{ backgroundColor: 'white' }}
                onPress={
                  !checklistItemResult
                    ? undefined
                    : editingChecklistItemId === checklistItem.id
                      ? () => setEditingChecklistItemId(undefined)
                      : () =>
                          animateNext(() => {
                            setEditingChecklistItemId(checklistItem.id);
                          })
                }
                navigationIcon={
                  editingChecklistItemId === checklistItem.id ||
                  !checklistItemResult
                    ? undefined
                    : resultIsTick
                      ? ICONS.CheckLight
                      : resultIsCross
                        ? ICONS.XmarkLight
                        : ICONS.DashLight
                }
                navigationIconColor={
                  resultIsTick
                    ? theme.colors.green
                    : resultIsCross
                      ? theme.colors.red
                      : resultIsNA
                        ? 'black'
                        : undefined
                }
              >
                <Txt>{checklistItem.text}</Txt>
                {(editingChecklistItemId === checklistItem.id ||
                  !checklistItemResult) && (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginTop: theme.padding,
                      backgroundColor: '#f1f1f1',
                      padding: 2,
                      borderRadius: 99,
                      gap: theme.padding / 2,
                    }}
                  >
                    <Button
                      icon={ICONS.CheckLight}
                      iconColor={resultIsTick ? 'white' : theme.colors.green}
                      backgroundColor={
                        resultIsTick ? theme.colors.green : 'transparent'
                      }
                      style={{ flex: 1 }}
                      styleHovered={{
                        backgroundColor: resultIsTick
                          ? theme.colors.green
                          : 'transparent',
                      }}
                      stylePressed={{
                        backgroundColor: resultIsTick
                          ? theme.colors.green
                          : 'transparent',
                      }}
                      onPress={() =>
                        handleSaveChecklistItemResult(
                          checklistItem.id,
                          ChecklistItemResultEnum.tick,
                        )
                      }
                    />
                    <Button
                      icon={ICONS.XmarkLight}
                      iconColor={resultIsCross ? 'white' : theme.colors.red}
                      backgroundColor={
                        resultIsCross ? theme.colors.red : 'transparent'
                      }
                      style={{ flex: 1 }}
                      styleHovered={{
                        backgroundColor: resultIsCross
                          ? theme.colors.red
                          : 'transparent',
                      }}
                      stylePressed={{
                        backgroundColor: resultIsCross
                          ? theme.colors.red
                          : 'transparent',
                      }}
                      onPress={() =>
                        handleSaveChecklistItemResult(
                          checklistItem.id,
                          ChecklistItemResultEnum.cross,
                        )
                      }
                    />
                    <Button
                      icon={ICONS.DashLight}
                      backgroundColor={resultIsNA ? '#999' : 'transparent'}
                      style={{ flex: 1 }}
                      iconColor={resultIsNA ? 'white' : 'black'}
                      styleHovered={{
                        backgroundColor: resultIsNA ? '#999' : 'transparent',
                      }}
                      stylePressed={{
                        backgroundColor: resultIsNA ? '#999' : 'transparent',
                      }}
                      onPress={() =>
                        handleSaveChecklistItemResult(
                          checklistItem.id,
                          ChecklistItemResultEnum.dash,
                        )
                      }
                    />
                    {!!checklistItemResult && (
                      <Button
                        icon={ICONS.TrashCanLight}
                        onPress={() =>
                          handleDeleteChecklistItemResult(checklistItem.id)
                        }
                        style={{ flex: 1 }}
                      />
                    )}
                  </View>
                )}
              </CardRow>
            );
          })}
        </Card>
      )}
    </Page>
  );
};
